import { Image01Icon } from '@/components/icons/Image01Icon';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { ArtifactMessageCard } from '@/modules/household/chat/components/messages/artifact/ArtifactMessageCard';
import {
  type HouseholdChatMessage,
  useHouseholdChatContext,
} from '@/modules/household/chat/contexts/householdChat.context';
import {
  serializeStack,
  TROWSER_PARAM,
  TrowserKey,
} from '@/modules/navigation/trowsers/trowser.types';
import { ROUTE_KEYS } from '@/navigation/constants';

/** Cards show historical snapshots and open the current saved resource. */
export function DiagramMessageCard({
  message,
}: {
  message: HouseholdChatMessage;
}) {
  const chat = useHouseholdChatContext();
  const { navigate } = useNavigateToRoute();
  const enabled = useFeatureFlag('diagram_builder');
  const canEdit = useCanEditHouseholdData({ returnFalseOnMissingUser: true });
  const { showFeedback } = useFeedback();
  const candidate = message.diagramContent;
  // The server sets diagram content on every DIAGRAM_MESSAGE. The field is
  // nullable only because all message types share the Message type.
  if (!candidate) return null;
  // Only a saved diagram can open in the editor.
  const artifactID = message.diagramArtifact?.id;
  // Open the editor modal, not the household chat. The modal loads the saved
  // diagram and shows the chat of its latest generation in its own sidebar.
  // The modal mounts on the Studio page and its open state is in the URL, so
  // this navigation works from any household page.
  const openDiagram = artifactID
    ? () => {
        void navigate(
          ROUTE_KEYS.HOUSEHOLD_ARTIFACT_BUILDER,
          { householdId: chat.householdId },
          {
            [TROWSER_PARAM]: serializeStack([
              { key: TrowserKey.Artifact, target: artifactID },
            ]),
          }
        );
      }
    : undefined;
  return (
    <ArtifactMessageCard
      // The Studio navigation item uses this icon for diagrams.
      icon={Image01Icon}
      title={message.diagramArtifact?.name ?? candidate.diagram.content.title}
      onOpen={enabled && canEdit ? openDiagram : undefined}
      // TODO: Download the diagram as a PNG. The builder export renders the
      // diagram surface, and the chat does not mount that surface yet.
      onDownload={() => {
        showFeedback('Diagram download is not available yet.', {
          variant: 'info-low',
        });
      }}
    />
  );
}
